import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { AlertTriangle, Check, Dice1, Dice2, Dice3, Dice4, Dice5, Dice6, Dices, Loader2, Pencil, Save, Sparkles, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AvatarGenerator } from '@/frontend/components/avatar-generator'
import { AvatarPreview } from '@/frontend/components/avatar-generator/AvatarPreview'
import { buildPromptFromSelections, randomizeConstrainedSelections } from '@/frontend/components/avatar-generator/avatarOptions'
import type { DigitalWorkerDetail } from '@/frontend/pages/Dashboard/DigitalWorkers/types'

const DICE_ICONS: LucideIcon[] = [Dice1, Dice2, Dice3, Dice4, Dice5, Dice6]
const DICE_ROLL_INTERVAL_MS = 180

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function pickDifferentDiceIndex(previous: number): number {
  let next = Math.floor(Math.random() * DICE_ICONS.length)
  if (next === previous) next = (next + 1) % DICE_ICONS.length
  return next
}

interface Props {
  worker: DigitalWorkerDetail
  canManage: boolean
  avatarGenerating?: boolean
  hideHeader?: boolean
  inlineGenerator?: boolean
  onPreviewChange?: (preview: { imageData: string; mimeType: string } | null) => void
  onBusyChange?: (busy: boolean) => void
}

export interface AvatarStudioHandle {
  /** Saves any unsaved generated avatar. Resolves true when saved (or nothing pending). */
  saveCurrent: () => Promise<boolean>
}

interface GeneratedAvatar {
  imageData: string
  mimeType: string
}

function SavedBanner({ workerName }: { workerName: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center gap-3 rounded-xl border border-violet-500/30 bg-gradient-to-br from-violet-50/70 via-background to-background px-3 py-2.5 shadow-sm animate-message-in dark:from-violet-500/10"
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-500/15 text-violet-600 ring-1 ring-violet-500/20 dark:text-violet-400">
        <Sparkles className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-foreground">Pronto! O rosto de {workerName} ficou ótimo.</p>
        <p className="truncate text-xs text-muted-foreground">Você pode trocar a qualquer momento.</p>
      </div>
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-violet-500 text-white shadow-sm">
        <Check className="h-3 w-3 animate-check-zoom-in" />
      </span>
    </div>
  )
}

function base64ToFile(imageData: string, mimeType: string, filename: string): File {
  const payload = imageData.includes(',') ? imageData.split(',').pop() ?? '' : imageData
  const binary = atob(payload)
  const bytes = new Uint8Array(binary.length)

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }

  return new File([bytes], filename, { type: mimeType })
}

const AvatarStudio = forwardRef<AvatarStudioHandle, Props>(function AvatarStudio({ worker, canManage, avatarGenerating = false, hideHeader = false, inlineGenerator = false, onPreviewChange, onBusyChange }, ref) {
  const [generatedAvatar, setGeneratedAvatar] = useState<GeneratedAvatar | null>(null)
  const [generating, setGenerating] = useState(false)
  const [inlineGenerating, setInlineGenerating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [customizeOpen, setCustomizeOpen] = useState(false)
  const [revealing, setRevealing] = useState(false)
  const [diceIndex, setDiceIndex] = useState<number | null>(null)
  const previousAvatarRef = useRef<string | null>(worker.avatar_url ?? null)
  const previewAnchorRef = useRef<HTMLDivElement | null>(null)

  // Bubble the unsaved generated preview up so the hero can show it live.
  useEffect(() => {
    if (!onPreviewChange) return
    onPreviewChange(generatedAvatar)
    return () => onPreviewChange(null)
  }, [generatedAvatar, onPreviewChange])

  useEffect(() => {
    onBusyChange?.(generating || inlineGenerating || saving)
  }, [generating, inlineGenerating, onBusyChange, saving])

  useEffect(() => {
    if (!generating) {
      setDiceIndex(null)
      return
    }
    if (prefersReducedMotion()) return

    let current = -1
    setDiceIndex(() => {
      current = pickDifferentDiceIndex(current)
      return current
    })
    const id = window.setInterval(() => {
      setDiceIndex(() => {
        current = pickDifferentDiceIndex(current)
        return current
      })
    }, DICE_ROLL_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [generating])

  useEffect(() => {
    const previous = previousAvatarRef.current
    const current = worker.avatar_url ?? null
    if (current && current !== previous) {
      setRevealing(true)
      const timer = window.setTimeout(() => setRevealing(false), 520)
      previousAvatarRef.current = current
      return () => window.clearTimeout(timer)
    }
    previousAvatarRef.current = current
  }, [worker.avatar_url])

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''

  // Auto-dismiss the celebration banner after a short window.
  useEffect(() => {
    if (!saved) return
    const id = window.setTimeout(() => setSaved(false), 3000)
    return () => window.clearTimeout(id)
  }, [saved])

  const saveAvatar = useCallback((avatar: GeneratedAvatar): Promise<boolean> => {
    setSaving(true)
    setSaved(false)
    setError(null)

    const mimeType = avatar.mimeType || 'image/png'
    const ext = mimeType.includes('png') ? 'png' : 'jpg'
    const file = base64ToFile(avatar.imageData, mimeType, `avatar.${ext}`)

    return new Promise<boolean>((resolve) => {
      router.post(`/dashboard/digital_workers/${worker.id}`, {
        _method: 'patch',
        avatar: file,
      }, {
        forceFormData: true,
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          setSaved(true)
          setGeneratedAvatar(null)
          resolve(true)
        },
        onError: () => {
          setError('Não foi possível salvar o avatar. Tente novamente.')
          resolve(false)
        },
        onFinish: () => setSaving(false),
      })
    })
  }, [worker.id])

  useImperativeHandle(ref, () => ({
    saveCurrent: async () => {
      if (generating || inlineGenerating) return false
      if (!generatedAvatar || saving) return true
      return saveAvatar(generatedAvatar)
    },
  }), [generatedAvatar, generating, inlineGenerating, saveAvatar, saving])

  const generateAvatar = useCallback(async () => {
    setGenerating(true)
    setSaved(false)
    setError(null)

    try {
      const selections = randomizeConstrainedSelections()
      const prompt = buildPromptFromSelections(selections)
      const response = await fetch('/dashboard/avatar_generator', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ prompt }),
      })
      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error('avatar_generation_failed')
      }

      setGeneratedAvatar({
        imageData: data.image_data,
        mimeType: data.mime_type || 'image/png',
      })
    } catch {
      setError('Não foi possível gerar uma sugestão agora.')
    } finally {
      setGenerating(false)
    }
  }, [csrfToken])

  const handleCustomizeSave = (imageData: string, mimeType: string) => {
    setCustomizeOpen(false)
    const avatar = { imageData, mimeType }
    setGeneratedAvatar(avatar)
    saveAvatar(avatar)
  }

  const showLoadingPreview = generating || (!generatedAvatar && !worker.avatar_url && avatarGenerating)

  return (
    <div className="space-y-5" data-testid="avatar-studio">
      {!inlineGenerator && (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div
            ref={previewAnchorRef}
            className={cn('shrink-0', revealing && 'animate-reveal-bounce')}
            data-testid={showLoadingPreview ? 'avatar-studio-loading' : 'avatar-studio-preview'}
            aria-busy={showLoadingPreview}
          >
            {generatedAvatar ? (
              <AvatarPreview imageData={generatedAvatar.imageData} mimeType={generatedAvatar.mimeType} isLoading={false} size={104} compact />
            ) : worker.avatar_url ? (
              <div className={cn('relative', !revealing && 'animate-avatar-breathing')}>
                <img
                  src={worker.avatar_url}
                  alt={`Avatar de ${worker.name}`}
                  className={cn(
                    'h-24 w-24 rounded-full border-4 border-background object-cover shadow-md ring-2 ring-violet-500/25 transition-all',
                    saved && 'ring-4 ring-violet-500/60',
                  )}
                />
                {revealing && (
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 rounded-full ring-4 ring-violet-400/30 animate-ping"
                  />
                )}
                {saved && (
                  <>
                    <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-full ring-4 ring-violet-400/40 animate-ping" />
                    <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-full ring-4 ring-fuchsia-400/30 animate-ping" style={{ animationDelay: '120ms' }} />
                  </>
                )}
              </div>
            ) : (
              <AvatarPreview imageData={null} mimeType="image/png" isLoading={showLoadingPreview} size={104} compact />
            )}
          </div>

          <div className="min-w-0 flex-1 space-y-2">
            {!hideHeader && (
              <div>
                <h3 className="text-base font-semibold text-foreground">Avatar do agente</h3>
                <p className="text-sm text-muted-foreground">Defina a aparência do agente enquanto o restante do lançamento acontece.</p>
              </div>
            )}
            {!canManage && (
              <p className="text-sm text-muted-foreground">Seu usuário pode visualizar o avatar, mas não pode alterar esta configuração.</p>
            )}
            {saved && <SavedBanner workerName={worker.name} />}
          </div>
        </div>
      )}

      {inlineGenerator && (!canManage || saved) && (
        <div className="space-y-2">
          {!canManage && (
            <p className="text-sm text-muted-foreground">Seu usuário pode visualizar o avatar, mas não pode alterar esta configuração.</p>
          )}
          {saved && <SavedBanner workerName={worker.name} />}
        </div>
      )}

      {error && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{error}</AlertTitle>
          <AlertDescription>Você pode tentar novamente ou personalizar manualmente.</AlertDescription>
        </Alert>
      )}

      {!inlineGenerator && (
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          <Button
            type="button"
            variant="outline"
            onClick={generateAvatar}
            disabled={!canManage || generating || saving}
            className={cn(
              'group relative overflow-hidden transition-all duration-200',
              'hover:-translate-y-[1px] hover:shadow-md',
              generating && 'ring-2 ring-violet-500/40',
            )}
            aria-label={error ? 'Tentar novamente' : worker.avatar_url ? 'Gerar nova sugestão' : 'Gerar sugestão'}
          >
            <span className="relative inline-flex h-4 w-4 items-center justify-center">
              {diceIndex !== null ? (
                (() => {
                  const ActiveDice = DICE_ICONS[diceIndex]
                  return (
                    <ActiveDice
                      key={diceIndex}
                      className={cn(
                        'absolute inset-0 h-4 w-4 text-violet-500 animate-dice-tumble',
                        'drop-shadow-[0_0_6px_rgba(139,92,246,0.6)]',
                      )}
                    />
                  )
                })()
              ) : (
                <Dices
                  className={cn(
                    'h-4 w-4 transition-transform duration-300',
                    'group-hover:rotate-12 group-hover:text-violet-500',
                  )}
                />
              )}
            </span>
            {error ? 'Tentar novamente' : worker.avatar_url ? 'Gerar nova sugestão' : 'Gerar sugestão'}
          </Button>
          <Button type="button" variant="outline" onClick={() => setCustomizeOpen(true)} disabled={!canManage || generating || saving}>
            <Pencil className="h-4 w-4" />
            Personalizar
          </Button>
          {generatedAvatar && (
            <Button type="button" onClick={() => saveAvatar(generatedAvatar)} disabled={!canManage || saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Salvar avatar
            </Button>
          )}
        </div>
      )}

      {inlineGenerator ? (
        <div data-testid="avatar-generator-inline" ref={previewAnchorRef}>
          <AvatarGenerator
            onSave={handleCustomizeSave}
            onImageGenerated={(imageData, mimeType) => setGeneratedAvatar({ imageData, mimeType })}
            onLoadingChange={setInlineGenerating}
            compact
            hideSaveButton
            disabled={!canManage || saving}
            initialImageUrl={worker.avatar_url}
            previewLoading={avatarGenerating}
            previewAlt={`Avatar de ${worker.name}`}
          />
        </div>
      ) : (
        <Dialog open={customizeOpen} onOpenChange={setCustomizeOpen}>
          <DialogContent className="max-w-[800px]">
            <DialogHeader><DialogTitle>Personalizar avatar</DialogTitle></DialogHeader>
            <AvatarGenerator onSave={handleCustomizeSave} compact />
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
})

export default AvatarStudio
